import { Button, DeleteIcon, EditIcon } from "@bp/ui-components";
import { useState } from "react";
import { useMutation } from "urql";
import { graphql } from "../../../client/gql";
import { useConfirm } from "../../../hooks/useConfirm";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import { EditProfileModal } from "../../Organizations/modals/EditProfileModal";

const ProfileActionsMenu_DetachProfile = graphql(`
  mutation ProfileActionsMenu_DetachProfile($where: ProfileWhere, $update: ProfileUpdateInput) {
    updateProfiles(where: $where, update: $update) {
      profiles {
        uuid
      }
    }
  }
`);

const ProfileActionsMenu_DeleteProfile = graphql(`
  mutation ProfileActionsMenu_DeleteProfile($where: ProfileWhere) {
    deleteProfiles(where: $where) {
      __typename
    }
  }
`);

export type ActionableProfile = {
  uuid: string;
  /** Display name for dialogs and toasts. */
  name: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  /** Current org-directed role name (TEACHER/STUDENT/…), if any. */
  role?: string | null;
  org: { uuid: string; name: string };
};

type Props = {
  profile: ActionableProfile;
  /** Account the profile hangs off — needed to disconnect it. */
  userUuid: string;
  /** Warn before deleting the person's only remaining profile. */
  isLastProfile: boolean;
  onChanged: () => void;
};

/**
 * Actions for one org profile of a person, as direct buttons: edit, detach,
 * delete. Product access has its own card. Detaching leaves the profile in
 * place but without an account ("Kein Konto").
 */
export function ProfileActions({ profile, userUuid, isLastProfile, onChanged }: Props) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [, detachProfile] = useMutation(ProfileActionsMenu_DetachProfile);
  const [, deleteProfile] = useMutation(ProfileActionsMenu_DeleteProfile);
  const { confirm, ConfirmationDialog } = useConfirm();

  const onDetach = async () => {
    if (busy) {
      return;
    }
    const ok = await confirm({
      title: "Profil vom Konto lösen",
      message: `„${profile.name}" in ${profile.org.name} bleibt bestehen, verliert aber die Verbindung zum Login-Konto und erscheint danach als „Kein Konto".`,
      confirmText: "Lösen",
      cancelText: "Abbrechen",
    });
    if (!ok) {
      return;
    }
    setBusy(true);
    const res = await detachProfile({
      where: { uuid: profile.uuid },
      update: { user: { disconnect: { where: { node: { uuid: userUuid } } } } },
    });
    setBusy(false);
    if (res.error) {
      showUserErrorToast({ text: "Lösen fehlgeschlagen", error: res.error });
      return;
    }
    showSuccessToast("Profil vom Konto gelöst");
    onChanged();
  };

  const onDelete = async () => {
    if (busy) {
      return;
    }
    const ok = await confirm({
      title: "Profil löschen",
      message: isLastProfile
        ? `„${profile.name}" wird aus ${profile.org.name} entfernt. Es ist das letzte Profil dieser Person — das Login-Konto bleibt bestehen, gehört danach aber zu keiner Organisation mehr.`
        : `„${profile.name}" wird aus ${profile.org.name} entfernt. Das Login-Konto und die übrigen Profile bleiben bestehen.`,
      confirmText: "Löschen",
      cancelText: "Abbrechen",
    });
    if (!ok) {
      return;
    }
    setBusy(true);
    const res = await deleteProfile({ where: { uuid: profile.uuid } });
    setBusy(false);
    if (res.error) {
      showUserErrorToast({ text: "Löschen fehlgeschlagen", error: res.error });
      return;
    }
    showSuccessToast(`${profile.name} aus ${profile.org.name} entfernt`);
    onChanged();
  };

  return (
    <>
      <Button hierarchy="secondary" icon={<EditIcon />} onClick={() => setEditing(true)}>
        Bearbeiten
      </Button>
      <Button hierarchy="secondary" disabled={busy} onClick={() => void onDetach()}>
        Vom Konto lösen
      </Button>
      <Button
        hierarchy="secondary"
        icon={<DeleteIcon />}
        disabled={busy}
        onClick={() => void onDelete()}
      >
        Löschen
      </Button>

      {editing && (
        <EditProfileModal
          profile={{
            uuid: profile.uuid,
            firstName: profile.firstName,
            lastName: profile.lastName,
            email: profile.email,
            orgUuid: profile.org.uuid,
            role: profile.role,
          }}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
        />
      )}
      <ConfirmationDialog />
    </>
  );
}
